import {
  FlexBox,
  Grid,
  Qrcode,
  space,
  Table,
  theme,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { useTranslation } from "react-i18next";
import styledComponents from "styled-components";

import { ReactComponent as TimeIcon } from "../../public/material-icons/access-time.svg";
import { ReactComponent as ArrowIcon } from "../../public/material-icons/arrow-forward.svg";
import { ReactComponent as CodeIcon } from "../../public/material-icons/code.svg";
import { ReactComponent as InfoIcon } from "../../public/material-icons/info-outline.svg";
import { ReactComponent as Locationicon } from "../../public/material-icons/location-on.svg";
import { ReactComponent as PhoneIcon } from "../../public/material-icons/phone-forwarded.svg";
import {
  formatDateWithSuffix,
  getTimeDifference,
} from "../util/time-formatter";
import type { Device, Incident } from "../view-model";

type ErrorReportTwoProps = {
  incident: Incident;
  device?: Device;
  comment: string;
  setComment: (value: string) => void;
};

const StyledFixedHeight = styledComponents.div`
  height: ${theme.font.lineHeight.default.base};
`;

const StyledDivider = styledComponents.div`
  border: 1px solid ${theme.colorPalette.grey200};
  margin-top: ${space(4)};
  margin-bottom: ${space(8)};
`;

const StyledTextarea = styledComponents.textarea`
  width: 100%;
  min-height: 80px;
  padding: ${space(2)};
  font-family: inherit;
  font-size: ${theme.font.size.default.sm};
  border: 1px solid ${theme.colorPalette.grey300};
  border-radius: 4px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${theme.color.primary};
  }
`;

export const ErrorReportTwo = ({
  comment,
  device,
  incident,
  setComment,
}: ErrorReportTwoProps) => {
  const { t } = useTranslation();

  const QrValue = `MATMSG:TO:
service@waldner.de;
SUB:
${t("email.sub")} #${incident.code};
BODY:
${t("email.greeting")}
${t([`${incident.message}`, "unknown.message"], {
  ns: "incidents",
})}

${t("email.placeholderAdditionalInfo")}

${t("email.devicedata")}:
${t("email.errorcode")}: #${incident.code}
${t("email.start")}: ${formatDateWithSuffix(new Date(incident.createdAt))}
${`${t("email.end")}: ${
  incident.closedAt
    ? formatDateWithSuffix(new Date(incident.closedAt))
    : t("email.erroractive")
}`}
${t("email.serial")}: ${device?.serial}
${t("report.canid")}: ${device?.id.split("-")[1]}
${t("email.project")}: ${device?.project}
${t("email.model")}: ${device?.model}
${t("email.position")}: ${`${device?.location.room || ""}, ${
    device?.location.position || ""
  }`}
;;
`;

  return (
    <>
      <Typography size="xl" weight="semiBold">
        {`${t("report.contactwaldner")}`}
      </Typography>
      <StyledDivider />
      <Typography size="lg" weight="semiBold">
        {t("report.telephonetext")}
      </Typography>
      <Grid columnGap="0">
        <Grid.Column width={6}>
          <Table>
            <Table.Body>
              <Table.Row padding={`${space(8)} ${space(5)} ${space(8)} 0`}>
                <Table.Data>
                  <FlexBox alignItems="center" gap={space(1)}>
                    <PhoneIcon
                      color={theme.colorPalette.grey400}
                      fontSize={theme.font.size.default.base}
                    />
                    <Typography color="black" size="xs" weight="semiBold">
                      {t("report.telephone")}
                    </Typography>
                  </FlexBox>
                </Table.Data>
                <Table.Data>
                  <Typography> {t("report.telephone.number")}</Typography>
                </Table.Data>
              </Table.Row>
              <Table.Row padding={`${space(1)} ${space(4)} ${space(1)} 0`}>
                <Table.Data>
                  <FlexBox alignItems="center" gap={space(1)}>
                    <CodeIcon
                      color={theme.colorPalette.grey400}
                      fontSize={theme.font.size.default.base}
                    />
                    <Typography color="black" size="xs" weight="semiBold">
                      {t("header.errorcode")}
                    </Typography>
                  </FlexBox>
                </Table.Data>
                <Table.Data>
                  <Typography size="sm">#{incident?.code}</Typography>
                </Table.Data>
              </Table.Row>
              <Table.Row padding={`${space(1)} ${space(4)} ${space(1)} 0`}>
                <Table.Data>
                  <FlexBox alignItems="center" gap={space(1)}>
                    <TimeIcon
                      color={theme.colorPalette.grey400}
                      fontSize={theme.font.size.default.base}
                    />
                    <Typography color="black" size="xs" weight="semiBold">
                      {t("report.time")}
                    </Typography>
                  </FlexBox>
                </Table.Data>
                <Table.Data>
                  <FlexBox
                    gap={space(3)}
                    justifyContent="space-between"
                    width="fit-content"
                  >
                    <FlexBox flexDirection="column">
                      <Typography size="sm">{t("report.start")}:</Typography>
                      {incident.closedAt && (
                        <Typography size="sm">{t("report.end")}:</Typography>
                      )}
                      <Typography size="sm">{t("report.duration")}:</Typography>
                    </FlexBox>
                    <FlexBox flexDirection="column">
                      <Typography size="sm">
                        {formatDateWithSuffix(new Date(incident.createdAt))}
                      </Typography>
                      {incident.closedAt && (
                        <Typography size="sm">
                          {formatDateWithSuffix(new Date(incident.closedAt))}
                        </Typography>
                      )}
                      <Typography size="sm">
                        {incident.closedAt
                          ? getTimeDifference(
                              new Date(incident.createdAt),
                              new Date(incident.closedAt)
                            )
                          : getTimeDifference(
                              new Date(incident.createdAt),
                              new Date()
                            )}
                      </Typography>
                    </FlexBox>
                  </FlexBox>
                </Table.Data>
              </Table.Row>
            </Table.Body>
          </Table>
        </Grid.Column>
        <Grid.Column width={6}>
          <Table>
            <Table.Body>
              <Table.Row padding={`${space(8)} ${space(5)} ${space(8)} 0`}>
                <Table.Data>
                  <StyledFixedHeight />
                </Table.Data>
                <Table.Data>
                  <StyledFixedHeight />
                </Table.Data>
              </Table.Row>
              <Table.Row padding={`${space(1)} ${space(4)} ${space(1)} 0`}>
                <Table.Data>
                  <FlexBox alignItems="center" gap={space(1)}>
                    <ArrowIcon
                      color={theme.colorPalette.grey400}
                      fontSize={theme.font.size.default.base}
                    />
                    <Typography color="black" size="xs" weight="semiBold">
                      {t("report.project")}
                    </Typography>
                  </FlexBox>
                </Table.Data>
                <Table.Data>
                  <Typography size="sm">{device?.project}</Typography>
                </Table.Data>
              </Table.Row>
              <Table.Row padding={`${space(1)} ${space(4)} ${space(1)} 0`}>
                <Table.Data>
                  <FlexBox alignItems="center" gap={space(1)}>
                    <Locationicon
                      color={theme.colorPalette.grey400}
                      fontSize={theme.font.size.default.base}
                    />
                    <Typography color="black" size="xs" weight="semiBold">
                      {t("report.location")}
                    </Typography>
                  </FlexBox>
                </Table.Data>
                <Table.Data>
                  <Typography size="sm">
                    {`${device?.location.position || ""}`}
                  </Typography>
                </Table.Data>
              </Table.Row>
              <Table.Row padding={`${space(1)} ${space(4)} ${space(1)} 0`}>
                <Table.Data>
                  <FlexBox alignItems="center" gap={space(1)}>
                    <InfoIcon
                      color={theme.colorPalette.grey400}
                      fontSize={theme.font.size.default.base}
                    />
                    <Typography color="black" size="xs" weight="semiBold">
                      {t("report.device.model")}
                    </Typography>
                  </FlexBox>
                </Table.Data>
                <Table.Data>
                  <Typography size="sm">{device?.model}</Typography>
                </Table.Data>
              </Table.Row>
              <Table.Row padding={`${space(1)} ${space(4)} ${space(1)} 0`}>
                <Table.Data>
                  <FlexBox alignItems="center" gap={space(1)}>
                    <ArrowIcon
                      color={theme.colorPalette.grey400}
                      fontSize={theme.font.size.default.base}
                    />
                    <Typography color="black" size="xs" weight="semiBold">
                      {t("report.serial")}
                    </Typography>
                  </FlexBox>
                </Table.Data>
                <Table.Data>
                  <Typography size="sm">{device?.serial}</Typography>
                </Table.Data>
              </Table.Row>
              <Table.Row padding={`${space(1)} ${space(4)} ${space(1)} 0`}>
                <Table.Data>
                  <FlexBox alignItems="center" gap={space(1)}>
                    <ArrowIcon
                      color={theme.colorPalette.grey400}
                      fontSize={theme.font.size.default.base}
                    />
                    <Typography color="black" size="xs" weight="semiBold">
                      {t("report.canid")}
                    </Typography>
                  </FlexBox>
                </Table.Data>
                <Table.Data>
                  <Typography size="sm">{device?.id.split("-")[1]}</Typography>
                </Table.Data>
              </Table.Row>
            </Table.Body>
          </Table>
        </Grid.Column>
      </Grid>
      <StyledDivider />
      <FlexBox flexDirection="column" gap={space(4)}>
        <Typography size="lg" weight="semiBold">
          {t("report.emailtext")}
        </Typography>
        <Qrcode value={QrValue} />
      </FlexBox>
      <StyledDivider />
      <FlexBox flexDirection="column" gap={space(1)}>
        <Typography color="black" size="sm" weight="semiBold">
          {t("report.commentlabel")}
        </Typography>
        <StyledTextarea
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("report.commentplaceholder")}
          value={comment}
        />
      </FlexBox>
    </>
  );
};
